import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { useMutation } from '@apollo/client';
import { useInterpret, useSelector } from '@xstate/react';
import { updatePredictiveAudienceGoalMutation } from 'api/mutations/predictiveAudiences';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { useLazyGraphql } from 'utils/hooks/useLazyGraphql';
import { fetchPreviewUsers } from 'api/queries/predictiveAudiences';
import { useCoreGoalsContext } from 'modules/predictiveGoals/components/contexts/CoreGoalsContext/CoreGoalsContext';
import {
  EditPredictiveGoalResponse,
  EditPredictiveGoalVariables,
  PredictiveAudienceGoal,
} from '../../../../types';
import {
  MutateGoalStates,
  createMutateStateMachineEffects,
  mutateGoalStateMachine,
} from '../../hooks/useMutateGoal.state';
import { GOAL_UPDATE_RETRAIN_CONFIRMATION, GOAL_UPDATE_CONFIRMATION } from '../../../../constants';
import { reportGoalEditError } from '../../../../observabilityUtils';

export interface UseEditGoal {
  hasCriteriaChanged: boolean;
  onPreviewComplete: () => void;
  onPreviewError: () => void;
  setGoal: (updatedGoal: PredictiveAudienceGoal) => void;
  setHasUpdateError: (setter: boolean) => void;
  setUnsavedChanges: (setter: boolean) => void;
}

export const useEditGoal = ({
  hasCriteriaChanged,
  onPreviewComplete,
  onPreviewError,
  setGoal,
  setHasUpdateError,
  setUnsavedChanges,
}: UseEditGoal) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { fetch: queryPreviewUsers } = useLazyGraphql(fetchPreviewUsers, {
    fetchPolicy: 'no-cache',
  });
  const [updateGoal] = useMutation<EditPredictiveGoalResponse, EditPredictiveGoalVariables>(
    updatePredictiveAudienceGoalMutation,
  );
  const {
    goals: { retrigger },
  } = useCoreGoalsContext();
  const effects = useMemo(
    () =>
      createMutateStateMachineEffects<EditPredictiveGoalResponse, EditPredictiveGoalVariables>({
        fetchGoalPreview: queryPreviewUsers,
        mutateGoal: updateGoal,
        onMutationError: (graphqlError) => {
          reportGoalEditError(graphqlError);
          setHasUpdateError(true);
          setUnsavedChanges(true);
          dispatch(addNegativeToast(graphqlError?.message || 'Unable to save goal'));
        },
        onMutateComplete: ({ predictiveAudienceGoalUpdate }) => {
          retrigger();
          setGoal(predictiveAudienceGoalUpdate);
          setUnsavedChanges(false);
          const confirmationMessage = hasCriteriaChanged
            ? GOAL_UPDATE_RETRAIN_CONFIRMATION
            : GOAL_UPDATE_CONFIRMATION;
          dispatch(addPositiveToast(confirmationMessage));
          setHasUpdateError(false);
          history.push(`/predictive-audiences/goals/${predictiveAudienceGoalUpdate.id}`);
        },
        onPreviewComplete: () => {
          if (hasCriteriaChanged) {
            onPreviewComplete();
          }
        },
        onPreviewError,
        shouldConfirmMutation: hasCriteriaChanged,
      }),
    [hasCriteriaChanged, setGoal, setHasUpdateError, setUnsavedChanges],
  );
  const service = useInterpret(mutateGoalStateMachine, effects);
  const isUpdatingGoal = useSelector(service, (state) => {
    return [MutateGoalStates.previewGoal, MutateGoalStates.mutateGoal].some(state.matches);
  });
  const isUpdatePreviewInvalid = useSelector(service, (state) => {
    return state.matches(MutateGoalStates.previewInvalid);
  });
  const isUpdatePreviewError = useSelector(service, (state) => {
    return state.matches(MutateGoalStates.previewError);
  });
  const usersCount = useSelector(service, (state) => {
    return state.context.totalCount;
  });
  return {
    // Begin update cycle
    updateGoal: (goalVars: EditPredictiveGoalVariables) => {
      service.send(MutateGoalStates.previewGoal, {
        payload: goalVars,
      });
    },
    // Confirm update
    confirmUpdate: () => {
      service.send(MutateGoalStates.mutateGoal);
    },
    isUpdatingGoal,
    isUpdatePreviewError,
    isUpdatePreviewInvalid,
    service,
    usersCount,
  };
};
